import type { FC, ReactNode } from 'react'
import type { Consultation } from '@/lib/clinic/types'
import type { FollowUpInterval } from '@/constants/clinic'

import { CalendarCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appendToText, joinList } from '@/lib/clinic/format'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { FOLLOW_UP_INTERVALS, FOLLOW_UP_NOTE_SUGGESTIONS } from '@/constants/clinic'
import Field from '@/components/common/field'
import DateField from '@/components/common/date-field'
import SuggestionChips from '@/components/common/suggestion-chips'

import {
    addDays,
    addMonths,
    formatDate,
    formatDateTime,
    formatWeekday,
    relativeDay
} from '@/lib/clinic/datetime'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

export interface FollowUpFormValues {
    consultationId: string | null
    treatment: string
    dueDate: string | null
    notes: string
}

interface FollowUpFieldsProps {
    values: FollowUpFormValues
    onChange: (values: FollowUpFormValues) => void
    consultations: Consultation[]
    today: string
    lockConsultation?: boolean
    dueDateError?: string | null
    idPrefix?: string
}

const NOT_LINKED = 'not-linked'

export const treatmentSummary = (consultation: Consultation): string => {
    return joinList(consultation.treatments) || consultation.diagnosis
}

export const createFollowUpValues = (consultation: Consultation | null): FollowUpFormValues => ({
    consultationId: consultation?.id ?? null,
    treatment: consultation ? treatmentSummary(consultation) : '',
    dueDate: null,
    notes: ''
})

export const intervalToDate = (interval: FollowUpInterval, today: string): string => {
    if (interval.months) return addMonths(today, interval.months)

    return addDays(today, interval.days ?? 0)
}

export const consultationLabel = (consultation: Consultation): string => {
    const summary = treatmentSummary(consultation) || 'Consultation'

    return `${formatDateTime(consultation.consultedAt)} · ${summary}`
}

interface QuickDatePickerProps {
    value: string | null
    onChange: (value: string) => void
    today: string
    id?: string
    invalid?: boolean
}

export const QuickDatePicker: FC<QuickDatePickerProps> = ({ value, onChange, today, id, invalid = false }): ReactNode => {
    return (
        <div className='flex flex-col gap-2.5'>
            <div className='flex flex-wrap gap-1.5' role='group' aria-label='Quick returning dates'>
                {FOLLOW_UP_INTERVALS.map(interval => {
                    const date = intervalToDate(interval, today)
                    const active = value === date

                    return (
                        <button
                            key={interval.label}
                            type='button'
                            aria-pressed={active}
                            onClick={() => onChange(date)}
                            className={cn(
                                'h-8 rounded-lg border px-3 text-[13px] font-medium transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/40',
                                active
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-border bg-background text-foreground hover:border-primary/40 hover:bg-accent'
                            )}
                        >
                            {interval.label}
                        </button>
                    )
                })}
            </div>

            <DateField id={id} value={value} onChange={onChange} invalid={invalid} placeholder='Pick a date' />

            {value && (
                <p className='flex items-center gap-1.5 text-xs text-muted-foreground'>
                    <CalendarCheck className='size-3.5 text-primary' />
                    {formatWeekday(value)}, {formatDate(value)} · {relativeDay(value, today)}
                </p>
            )}
        </div>
    )
}

const FollowUpFields: FC<FollowUpFieldsProps> = ({
    values,
    onChange,
    consultations,
    today,
    lockConsultation = false,
    dueDateError,
    idPrefix = 'follow-up'
}): ReactNode => {
    const items = [
        { value: NOT_LINKED, label: 'Not linked to a consultation' },
        ...consultations.map(consultation => ({ value: consultation.id, label: consultationLabel(consultation) }))
    ]

    const linked = consultations.find(item => item.id === values.consultationId) ?? null

    return (
        <div className='flex flex-col gap-5'>
            {!lockConsultation && consultations.length > 0 && (
                <Field label='Related consultation' htmlFor={`${idPrefix}-consultation`}>
                    <Select
                        items={items}
                        value={values.consultationId ?? NOT_LINKED}
                        onValueChange={next => {
                            const consultationId = !next || next === NOT_LINKED ? null : next
                            const consultation = consultations.find(item => item.id === consultationId) ?? null

                            onChange({
                                ...values,
                                consultationId,
                                treatment: consultation ? treatmentSummary(consultation) : values.treatment
                            })
                        }}
                    >
                        <SelectTrigger id={`${idPrefix}-consultation`} className='w-full'>
                            <SelectValue />
                        </SelectTrigger>

                        <SelectContent alignItemWithTrigger={false}>
                            {items.map(item => (
                                <SelectItem key={item.value} value={item.value}>
                                    {item.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
            )}

            {lockConsultation && linked && (
                <div className='rounded-xl border bg-muted/40 px-3.5 py-3 text-[13px]'>
                    <p className='font-medium text-foreground'>
                        Linked to this consultation
                    </p>

                    <p className='mt-0.5 text-muted-foreground'>
                        {consultationLabel(linked)}
                    </p>
                </div>
            )}

            <Field label='Previous treatment' htmlFor={`${idPrefix}-treatment`}>
                <Input
                    id={`${idPrefix}-treatment`}
                    value={values.treatment}
                    onChange={event => onChange({ ...values, treatment: event.target.value })}
                    placeholder='e.g. Root Canal Treatment i.r.t 36'
                />
            </Field>

            <Field label='Returning date' htmlFor={`${idPrefix}-date`} required error={dueDateError}>
                <QuickDatePicker
                    id={`${idPrefix}-date`}
                    value={values.dueDate}
                    onChange={dueDate => onChange({ ...values, dueDate })}
                    today={today}
                    invalid={Boolean(dueDateError)}
                />
            </Field>

            <Field label='Follow-up notes' htmlFor={`${idPrefix}-notes`}>
                <Textarea
                    id={`${idPrefix}-notes`}
                    rows={3}
                    value={values.notes}
                    onChange={event => onChange({ ...values, notes: event.target.value })}
                    placeholder='What should happen at the next visit?'
                />

                <SuggestionChips
                    items={FOLLOW_UP_NOTE_SUGGESTIONS}
                    onPick={item => onChange({ ...values, notes: appendToText(values.notes, item) })}
                />
            </Field>
        </div>
    )
}

export default FollowUpFields
